import { useEffect, useState } from 'react';
import { apiGet } from '../../api/apiClient';
import {
    FLOW_GROUP_IDS,
    type FlowByGroup,
    type FlowGroupId,
    type FlowPoint,
} from './groups';

export type FlowScope =
    | { kind: 'outcome'; marketId: string }
    | { kind: 'event'; eventId: string };

interface RawPoint {
    ts: string;
    n_traders_yes: number;
    n_traders_no: number;
    capital_usd_yes: number;
    capital_usd_no: number;
}

interface RawResponse {
    market_id: string;
    group_name: string;
    points: RawPoint[];
}

export interface FlowSeriesResult {
    byGroup: FlowByGroup;
    loading: boolean;
    /** The request for this outcome and resolution has finished. */
    settled: boolean;
    available: boolean;
}

const EMPTY: FlowByGroup = new Map();

export function flowResolution(candleMinutes: number): string {
    if (candleMinutes <= 1) return '1m';
    if (candleMinutes <= 5) return '5m';
    if (candleMinutes <= 15) return '15m';
    return '1h';
}

export function toFlowPoints(points: readonly RawPoint[]): FlowPoint[] {
    return points
        .map(p => ({
            t: new Date(p.ts).getTime(),
            netCapital: p.capital_usd_yes - p.capital_usd_no,
            capitalYes: p.capital_usd_yes,
            capitalNo: p.capital_usd_no,
            wallets: p.n_traders_yes + p.n_traders_no,
        }))
        .filter(p => Number.isFinite(p.t))
        .sort((a, b) => a.t - b.t);
}

export function useFlowSeries(
    scope: FlowScope | null,
    candleMinutes: number
): FlowSeriesResult {
    const [byGroup, setByGroup] = useState<FlowByGroup>(EMPTY);
    const [loading, setLoading] = useState(false);
    // The request (outcome and resolution) that has finished. Compared with
    // the one asked for: the first render after a change comes before its
    // request has started.
    const [settledFor, setSettledFor] = useState<string | null>(null);

    const marketId = scope?.kind === 'outcome' ? scope.marketId : null;
    const resolution = flowResolution(candleMinutes);
    const requestKey = marketId ? `${marketId}|${resolution}` : null;

    useEffect(() => {
        // A new request is not settled by an earlier one's answer.
        setSettledFor(null);
        if (!marketId) {
            setByGroup(EMPTY);
            setLoading(false);
            return;
        }
        const controller = new AbortController();
        setLoading(true);
        setByGroup(EMPTY);

        Promise.all(
            FLOW_GROUP_IDS.map(group => {
                const params = new URLSearchParams({
                    group,
                    resolution,
                    limit: '2000',
                });
                return apiGet<RawResponse>(
                    `/api/signals/${encodeURIComponent(marketId)}/conviction?${params}`,
                    controller.signal
                )
                    .then((raw): [FlowGroupId, FlowPoint[]] => [
                        group,
                        toFlowPoints(raw.points ?? []),
                    ])
                    .catch((err): [FlowGroupId, FlowPoint[]] => {
                        if (err?.name !== 'AbortError') {
                            console.error(
                                `flow series failed (${group}):`,
                                err
                            );
                        }
                        return [group, []];
                    });
            })
        ).then(results => {
            if (controller.signal.aborted) return;
            setByGroup(new Map(results));
            setLoading(false);
            setSettledFor(`${marketId}|${resolution}`);
        });

        return () => controller.abort();
    }, [marketId, resolution]);

    return {
        byGroup,
        loading,
        settled: requestKey !== null && settledFor === requestKey,
        available: scope?.kind === 'outcome',
    };
}
